import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import './VisaChart.css';
import useDocumentTheme from '../hooks/useDocumentTheme';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const PREF_STORAGE_KEY = 'visa-chart-preferences';
const ALLOWED_RANGE_OPTIONS = [7, 14, 30];

// Explicitly exclude known spike snapshot days (can be adjusted later)
const EXCLUDED_DATES = ['2025-09-10', '2025-09-15'];

const SummaryCard = ({ title, value }) => (
    <div className="summary-card">
        <h4>{title}</h4>
        <p>{value}</p>
    </div>
);

const VisaChart = () => {
    const [chartData, setChartData] = useState(null);
    const [summaryStats, setSummaryStats] = useState(null);
    const [error, setError] = useState('');
    const [viewMode, setViewMode] = useState('daily'); // 'daily' | 'cumulative'
    const [rawData, setRawData] = useState(null); // { labels, approved, refused }
    const [rangeDays, setRangeDays] = useState(30); // 7 | 14 | 30
    const [hideOutliers, setHideOutliers] = useState(true);
    const [chartHeight, setChartHeight] = useState(500);
    const [tickLimit, setTickLimit] = useState(8);
    const theme = useDocumentTheme();
    const prefsHydrated = useRef(false);

    const cssVar = (name, fallback) => {
        if (typeof document === 'undefined') return fallback;
        const value = getComputedStyle(document.documentElement).getPropertyValue(name);
        return value ? value.trim() : fallback;
    };

    useEffect(() => {
        if (typeof window === 'undefined') {
            prefsHydrated.current = true;
            return;
        }
        try {
            const stored = window.localStorage.getItem(PREF_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed && (parsed.viewMode === 'daily' || parsed.viewMode === 'cumulative')) {
                    setViewMode(parsed.viewMode);
                }
                if (parsed && ALLOWED_RANGE_OPTIONS.includes(Number(parsed.rangeDays))) {
                    setRangeDays(Number(parsed.rangeDays));
                }
                if (parsed && typeof parsed.hideOutliers === 'boolean') {
                    setHideOutliers(parsed.hideOutliers);
                }
            }
        } catch (storageError) {
            console.warn('Unable to load visa chart preferences from storage', storageError);
        } finally {
            prefsHydrated.current = true;
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !prefsHydrated.current) {
            return;
        }
        try {
            const payload = {
                viewMode,
                rangeDays,
                hideOutliers,
            };
            window.localStorage.setItem(PREF_STORAGE_KEY, JSON.stringify(payload));
        } catch (storageError) {
            console.warn('Unable to persist visa chart preferences', storageError);
        }
    }, [viewMode, rangeDays, hideOutliers]);

    useEffect(() => {
        const onResize = () => {
            const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
            setChartHeight(w <= 400 ? 340 : w <= 768 ? 420 : 500);
            setTickLimit(w <= 400 ? 4 : w <= 768 ? 6 : 8);
        };
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        const loadData = async () => {
            try {
                setError('');

                // Fetch summary stats, decision deltas, and daily trends in parallel
                // Use 120s timeout to handle slow cold-start responses
                const axiosConfig = { timeout: 120000 };
                const [statsResponse, deltasResponse, trendsResponse] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/visa-stats`, axiosConfig),
                    axios.get(`${API_BASE_URL}/api/decision-deltas`, axiosConfig),
                    axios.get(`${API_BASE_URL}/api/daily-trends`, axiosConfig)
                ]);

                // Process summary stats
                const summary = statsResponse.data;
                setSummaryStats({
                    total_applications: summary.totalApplications || 0,
                    total_approved: summary.totalApproved || 0,
                    total_refused: summary.totalRefused || 0,
                    approval_rate: summary.approvalRate || 0,
                });

                // Prefer decision-deltas; if stale vs stats or daily-trends latest, fall back to dailyTrends.dailyDeltas
                const deltas = deltasResponse.data || {};
                const dailyTrends = trendsResponse.data || {};
                const ddLabels = Array.isArray(deltas.labels) ? deltas.labels : [];
                const ddLast = ddLabels.length ? String(ddLabels[ddLabels.length - 1]) : null;
                const statsLast = (summary.lastUpdated || '').split('T')[0];
                const dtDeltas = Array.isArray(dailyTrends.dailyDeltas) ? dailyTrends.dailyDeltas : [];
                const dtLast = dtDeltas.length ? String(dtDeltas[dtDeltas.length - 1].date) : null;

                let labels = [];
                let approved = [];
                let refused = [];

                const useTrendsFallback = !ddLast || (statsLast && ddLast < statsLast) || (dtLast && ddLast < dtLast);
                if (useTrendsFallback) {
                    labels = dtDeltas.map(d => String(d.date));
                    approved = dtDeltas.map(d => Number(d.approvedDelta || 0));
                    refused = dtDeltas.map(d => Number(d.refusedDelta || 0));
                } else {
                    const approvedSeries = Array.isArray(deltas.series) ? deltas.series.find(s => s.name === 'Approved') : null;
                    const refusedSeries = Array.isArray(deltas.series) ? deltas.series.find(s => s.name === 'Refused') : null;
                    labels = ddLabels;
                    approved = approvedSeries ? approvedSeries.data : [];
                    refused = refusedSeries ? refusedSeries.data : [];
                }

                // Keep raw for toggling daily/cumulative views
                setRawData({ labels, approved, refused });

                // Default Daily (bars)
                setChartData({
                    labels,
                    datasets: [
                        {
                            label: 'Approved',
                            data: approved,
                            backgroundColor: 'rgba(16, 185, 129, 0.8)',
                            borderColor: 'rgba(16, 185, 129, 1)',
                            borderWidth: 1,
                            borderRadius: 4,
                            maxBarThickness: 28
                        },
                        {
                            label: 'Refused',
                            data: refused,
                            backgroundColor: 'rgba(251, 113, 133, 0.8)',
                            borderColor: 'rgba(251, 113, 133, 1)',
                            borderWidth: 1,
                            borderRadius: 4,
                            maxBarThickness: 28
                        },
                    ],
                });

            } catch (err) {
                setError('Could not load visa data from server.');
                console.error(err);
            }
        };

        loadData();
    }, []);

    // Update chart when viewMode changes
    useEffect(() => {
        if (!rawData) return;
        const { labels, approved, refused } = rawData;

        // Apply explicit exclusions first
        const exclude = new Set(EXCLUDED_DATES.map(String));
        const labelsX = [];
        const approvedX = [];
        const refusedX = [];
        for (let i = 0; i < labels.length; i++) {
            const d = String(labels[i]);
            if (exclude.has(d)) continue;
            labelsX.push(d);
            approvedX.push(approved[i]);
            refusedX.push(refused[i]);
        }

        // Range filter (last N labels)
        const start = Math.max(0, labelsX.length - rangeDays);
        let subLabels = labelsX.slice(start);
        let subApproved = approvedX.slice(start);
        let subRefused  = refusedX.slice(start);

        // Outlier filtering on daily totals (hide initial spike snapshot days)
        if (hideOutliers && subLabels.length > 0) {
            const totals = subApproved.map((v, i) => Number(v || 0) + Number(subRefused[i] || 0));
            const positives = totals.filter(n => n > 0);
            const sorted = [...positives].sort((a, b) => a - b);
            const median = sorted.length
                ? (sorted[Math.floor((sorted.length - 1) / 2)] + sorted[Math.ceil((sorted.length - 1) / 2)]) / 2
                : 0;
            const factor = 8; // consider >8x median as spike
            if (median > 0) {
                const keep = totals.map(t => t <= median * factor);
                const filterByKeep = (arr) => arr.filter((_, i) => keep[i]);
                subLabels = filterByKeep(subLabels);
                subApproved = filterByKeep(subApproved);
                subRefused = filterByKeep(subRefused);
                // Ensure at least one point remains
                if (subLabels.length === 0) {
                    subLabels = labels.slice(-1);
                    subApproved = approved.slice(-1);
                    subRefused = refused.slice(-1);
                }
            }
        }

        const approvedBar = cssVar('--chart-approved-bar', 'rgba(16, 185, 129, 0.82)');
        const approvedBorder = cssVar('--chart-approved-border', 'rgba(16, 185, 129, 1)');
        const approvedFill = cssVar('--chart-approved-fill', 'rgba(16, 185, 129, 0.18)');
        const refusedBar = cssVar('--chart-refused-bar', 'rgba(251, 113, 133, 0.82)');
        const refusedBorder = cssVar('--chart-refused-border', 'rgba(251, 113, 133, 1)');
        const refusedFill = cssVar('--chart-refused-fill', 'rgba(251, 113, 133, 0.2)');
        const totalBorder = cssVar('--chart-total-border', 'rgba(99, 102, 241, 1)');
        const totalFill = cssVar('--chart-total-fill', 'rgba(99, 102, 241, 0.2)');

        if (viewMode === 'daily') {
            setChartData({
                labels: subLabels,
                datasets: [
                    {
                        label: 'Approved',
                        data: subApproved,
                        backgroundColor: approvedBar,
                        borderColor: approvedBorder,
                        borderWidth: 1,
                        borderRadius: 4,
                        maxBarThickness: 28
                    },
                    {
                        label: 'Refused',
                        data: subRefused,
                        backgroundColor: refusedBar,
                        borderColor: refusedBorder,
                        borderWidth: 1,
                        borderRadius: 4,
                        maxBarThickness: 28
                    },
                ],
            });
        } else {
            // Cumulative over filtered daily series
            const cum = (arr) => {
                const out = [];
                let acc = 0;
                for (let i = 0; i < arr.length; i++) {
                    const v = Number(arr[i] || 0);
                    acc += isNaN(v) ? 0 : v;
                    out.push(acc);
                }
                return out;
            };
            const approvedCumRaw = cum(subApproved);
            const refusedCumRaw  = cum(subRefused);
            const baseA = approvedCumRaw.length ? approvedCumRaw[0] : 0;
            const baseR = refusedCumRaw.length  ? refusedCumRaw[0]  : 0;
            const approvedCum = approvedCumRaw.map(v => v - baseA);
            const refusedCum  = refusedCumRaw.map(v => v - baseR);
            const totalCum    = approvedCum.map((v, i) => v + (refusedCum[i] || 0));

            setChartData({
                labels: subLabels,
                datasets: [
                    {
                        label: 'Approved (Cumulative)',
                        data: approvedCum,
                        borderColor: approvedBorder,
                        backgroundColor: approvedFill,
                        borderWidth: 2,
                        pointRadius: 2,
                        tension: 0.25,
                        fill: false
                    },
                    {
                        label: 'Refused (Cumulative)',
                        data: refusedCum,
                        borderColor: refusedBorder,
                        backgroundColor: refusedFill,
                        borderWidth: 2,
                        pointRadius: 2,
                        tension: 0.25,
                        fill: false
                    },
                    {
                        label: 'Total (Cumulative)',
                        data: totalCum,
                        borderColor: totalBorder,
                        backgroundColor: totalFill,
                        borderWidth: 2,
                        pointRadius: 2,
                        tension: 0.25,
                        fill: false
                    }
                ]
            });
        }
    }, [viewMode, rawData, rangeDays, hideOutliers]);

    const isLight = theme === 'light';

    const gridColor = cssVar('--chart-grid-color', isLight ? 'rgba(15, 118, 110, 0.18)' : 'rgba(148, 163, 184, 0.25)');
    const axisColor = cssVar('--chart-axis-color', isLight ? '#0f172a' : '#d1fae5');
    const legendColor = cssVar('--chart-legend-color', isLight ? '#065f46' : '#d1fae5');
    const tooltipBg = cssVar('--chart-tooltip-bg', isLight ? 'rgba(13, 148, 136, 0.9)' : 'rgba(2, 32, 24, 0.92)');

    const dailyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    font: {
                        size: 14,
                        weight: 'bold'
                    },
                    padding: 20,
                    color: legendColor
                }
            },
            title: {
                display: false
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                backgroundColor: tooltipBg,
                titleFont: { size: 14 },
                bodyFont: { size: 14 },
                padding: 12,
                displayColors: true,
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += new Intl.NumberFormat().format(context.parsed.y);
                        }
                        return label;
                    },
                    footer: function(items) {
                        if (!Array.isArray(items) || items.length === 0) {
                            return [];
                        }
                        const total = items.reduce((acc, item) => acc + (Number(item.parsed?.y) || 0), 0);
                        return total === 0 ? ['No decisions changed on this date.'] : [];
                    },
                    afterBody: function() {
                        return [];
                    }
                }
            }
        },
        scales: { 
            x: { 
                stacked: true,
                grid: { color: gridColor },
                ticks: {
                    maxRotation: 0,
                    minRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: tickLimit,
                    color: axisColor
                },
                border: { color: gridColor }
            },
            y: { 
                stacked: true, 
                beginAtZero: true,
                ticks: {
                    color: axisColor,
                    callback: function(value) {
                        return new Intl.NumberFormat().format(value);
                    }
                },
                grid: {
                    color: gridColor
                },
                border: { color: gridColor }
            }
        },
        animation: {
            duration: 1000,
            easing: 'easeInOutQuart'
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        }
    };

    const cumulativeOptions = {
        ...dailyOptions,
        plugins: {
            ...dailyOptions.plugins,
            title: {
                ...dailyOptions.plugins.title,
                display: false
            }
        },
        scales: {
            x: { ...dailyOptions.scales.x, stacked: false },
            y: { ...dailyOptions.scales.y, stacked: false }
        }
    };

    return (
        <div className="chart-container">
            {summaryStats && (
                <div className="summary-container">
                    <SummaryCard 
                        title="Total Applications" 
                        value={(summaryStats.total_applications || 0).toLocaleString()} 
                    />
                    <SummaryCard 
                        title="Total Approved" 
                        value={(summaryStats.total_approved || 0).toLocaleString()}
                    />
                    <SummaryCard 
                        title="Total Refused" 
                        value={(summaryStats.total_refused || 0).toLocaleString()}
                    />
                    <SummaryCard 
                        title="Approval Rate" 
                        value={`${typeof summaryStats.approval_rate === 'number' ? summaryStats.approval_rate.toFixed(2) : '0.00'}%`}
                    />
                </div>
            )}

            {/* Chart Insight Banner */}
            {summaryStats && summaryStats.approval_rate && (
                <div className="chart-insight-banner">
                    <span className="insight-icon">📈</span>
                    <div className="insight-content">
                        <strong>Current Approval Trend</strong>
                        <p>
                            {summaryStats.approval_rate >= 70 
                                ? `Strong approval rate at ${summaryStats.approval_rate.toFixed(1)}% - Processing is favorable`
                                : summaryStats.approval_rate >= 50
                                ? `Moderate approval rate at ${summaryStats.approval_rate.toFixed(1)}% - Stay informed with alerts`
                                : `Approval rate at ${summaryStats.approval_rate.toFixed(1)}% - Monitor your application closely`
                            }
                        </p>
                    </div>
                </div>
            )}
            
            <div className={isLight ? 'chart-shell light' : 'chart-shell'}>
                {error ? (
                    <div className="chart-state chart-error">
                        <div className="chart-icon" aria-hidden="true">⚠️</div>
                        <h3>Error Loading Data</h3>
                        <p>{error}</p>
                        <button 
                            onClick={() => window.location.reload()}
                            className="chart-retry"
                        >
                            Retry
                        </button>
                    </div>
                ) : !chartData ? (
                    <div className="chart-state chart-loading">
                        <div className="chart-skeleton" style={{ height: `${chartHeight}px` }}>
                            <div className="chart-skeleton-header">
                                <div className="chart-skeleton-title shimmer" />
                                <div className="chart-skeleton-pills">
                                    {['Daily', 'Cumulative', '7d', '14d', '30d'].map((label) => (
                                        <span key={label} className="chart-skeleton-pill shimmer" />
                                    ))}
                                </div>
                            </div>
                            <div className="chart-skeleton-body">
                                {Array.from({ length: Math.min(tickLimit + 2, 9) }).map((_, idx) => (
                                    <div
                                        key={idx}
                                        className="chart-skeleton-bar shimmer"
                                        style={{ height: `${40 + (idx % 5) * 10}%` }}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="loading-spinner" aria-hidden="true"></div>
                        <h3>Loading Visa Data...</h3>
                        <p>Please wait while we fetch the latest information</p>
                    </div>
                ) : (<>
                    {/* Header row: title + controls */}
                    <div className="chart-header">
                        <h3>
                            {viewMode === 'daily' ? `Visa Decisions - Daily Deltas (Per Day, Last ${rangeDays} days)` : `Visa Decisions - Cumulative (Last ${rangeDays} days)`}
                        </h3>
                        <div className="chart-controls">
                            <button
                                onClick={() => setViewMode('daily')}
                                className={`chart-toggle ${viewMode === 'daily' ? 'active' : ''}`}
                                data-tooltip="See daily changes"
                            >
                                Daily New
                            </button>
                            <button
                                onClick={() => setViewMode('cumulative')}
                                className={`chart-toggle ${viewMode === 'cumulative' ? 'active' : ''}`}
                                data-tooltip="See total progress"
                            >
                                Cumulative
                            </button>
                            <div className="chart-divider" />
                            <button
                                onClick={() => setRangeDays(7)}
                                className={`chart-toggle ${rangeDays === 7 ? 'selected' : ''}`}
                                data-tooltip="Last 7 days"
                            >
                                7d
                            </button>
                            <button
                                onClick={() => setRangeDays(14)}
                                className={`chart-toggle ${rangeDays === 14 ? 'selected' : ''}`}
                                data-tooltip="Last 14 days"
                            >
                                14d
                            </button>
                            <button
                                onClick={() => setRangeDays(30)}
                                className={`chart-toggle ${rangeDays === 30 ? 'selected' : ''}`}
                                data-tooltip="Last 30 days"
                            >
                                30d
                            </button>
                            <label className="chart-checkbox" data-tooltip="Remove unusual data spikes">
                                <input type="checkbox" checked={hideOutliers} onChange={(e) => setHideOutliers(e.target.checked)} /> Hide spike days
                            </label>
                        </div>
                    </div>

                    <div style={{ height: `${chartHeight}px`, width: '100%', overflow: 'visible' }}>
                        {viewMode === 'daily' ? (
                            <Bar 
                                options={{
                                    ...dailyOptions,
                                    maintainAspectRatio: false
                                }} 
                                data={chartData} 
                            />
                        ) : (
                            <Line 
                                options={{
                                    ...cumulativeOptions,
                                    maintainAspectRatio: false
                                }} 
                                data={chartData} 
                            />
                        )}
                    </div>
                </>)}
            </div>
            
        </div>
    );
};

export default VisaChart;
